import type { PatientListItem } from '@dev-console/types';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { Column } from '@/components/ui/data-table';
import { DataTable } from '@/components/ui/data-table';
import { Pagination } from '@/components/ui/pagination';
import { DataTableSkeleton, EmptyState, ErrorState, LoadingOverlay } from '@/components/ui/state-views';
import { searchPatients } from '@/config/api-helpers';
import { useDebounced } from '@/hooks/useDebounced';
import { formatDate } from '@/utils/format';

const DEFAULT_PAGINATION = { page: 1, pageSize: 25, totalCount: 0, totalPages: 1 };

function statusLabel(p: PatientListItem) {
  if (p.archivedAt) return 'Archived';
  return p.isActive ? 'Active' : 'Inactive';
}

const columns: Column<PatientListItem>[] = [
  {
    key: 'name',
    header: 'Patient',
    render: (p) => <span className='font-medium text-neutral-900'>{p.displayName}</span>,
  },
  { key: 'patientId', header: 'Patient ID', render: (p) => <span className='text-neutral-700'>{p.patientId}</span> },
  { key: 'userId', header: 'User ID', render: (p) => <span className='text-neutral-700'>{p.userId}</span> },
  {
    key: 'healthieId',
    header: 'Healthie ID',
    render: (p) => <span className='text-neutral-700'>{p.healthieId ?? '\u2014'}</span>,
  },
  { key: 'status', header: 'Status', render: (p) => <span className='text-neutral-700'>{statusLabel(p)}</span> },
  { key: 'phase', header: 'Phase', render: (p) => <span className='text-neutral-700'>{p.phaseOfCare}</span> },
  {
    key: 'created',
    header: 'Created',
    render: (p) => <span className='text-neutral-700'>{formatDate(p.createdAt)}</span>,
  },
];

export function PatientList() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const query = useDebounced(searchQuery, 300);

  useEffect(() => setPage(1), [query]);

  const { data, error, isFetching, isLoading, refetch } = useQuery({
    queryKey: ['patients', query, page],
    queryFn: () => searchPatients({ query, page, pageSize: 25 }),
    enabled: !!query.trim(),
  });

  const items = data?.items ?? [];
  const pagination = data?.pagination ?? DEFAULT_PAGINATION;

  const pageSummary = useMemo(() => {
    if (pagination.totalCount === 0) return 'No patients found';
    const start = (pagination.page - 1) * pagination.pageSize + 1;
    const end = Math.min(start + items.length - 1, pagination.totalCount);
    return `Showing ${start}\u2013${end} of ${pagination.totalCount} patients`;
  }, [items.length, pagination.page, pagination.pageSize, pagination.totalCount]);

  const isInitialLoading = isLoading && items.length === 0 && !error;
  const showOverlay = isFetching && items.length > 0;
  const hasLoadedResults = data != null && !isInitialLoading && !error;

  return (
    <div className='space-y-6'>
      <h2 className='font-mediumbold text-xl text-neutral-900'>Patients</h2>

      {error && (
        <ErrorState message={error?.message ?? ''} onRetry={() => refetch()} title='Patients are unavailable' />
      )}

      <input
        className='w-full rounded-md border border-neutral-200 px-3 py-2 text-sm text-neutral-900 focus:border-neutral-400 focus:outline-none'
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder='Search by patient ID, user ID, or Healthie ID'
        value={searchQuery}
      />

      {!query.trim() ? (
        <div className='rounded-lg border border-neutral-200 bg-white px-4 py-6 text-sm text-neutral-500'>
          Search by patient ID, user ID, or Healthie ID to find patients.
        </div>
      ) : error ? null : isInitialLoading ? (
        <div className='rounded-lg border border-neutral-200 bg-white'>
          <DataTableSkeleton columns={columns.length} label='Loading patients' />
        </div>
      ) : hasLoadedResults ? (
        <div className='relative rounded-lg border border-neutral-200 bg-white'>
          <div className='flex items-center justify-between border-b border-neutral-100 px-4 py-3'>
            <p className='text-sm text-neutral-600'>{pageSummary}</p>
            <p className='text-sm text-neutral-500'>Page {pagination.page}</p>
          </div>

          {items.length === 0 ? (
            <EmptyState message='No patients match this query.' />
          ) : (
            <DataTable
              columns={columns}
              data={items}
              keyFn={(p) => p.patientId}
              onRowClick={(p) => navigate(`/patients/${p.patientId}`)}
            />
          )}

          {showOverlay && <LoadingOverlay label='Refreshing patients' />}

          <Pagination
            page={pagination.page}
            totalPages={pagination.totalPages}
            onPageChange={setPage}
            disabled={isLoading}
          />
        </div>
      ) : null}
    </div>
  );
}
